import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatFilterText } from '../../../../utils/formatFilterText';
import { parseTabTags, tabHasTag } from '../../../../utils/parseTabTags';
import styles from './index.module.scss';
import {
  setCurrentTab,
  resetFilters,
  setSkipFilterReset,
} from '../../../../redux/slices/currentTabSlice';
import { setPaginationPage } from '../../../../redux/slices/paginationSlice';
import {
  setFilterAValueWithUpdate,
  setFilterBValueWithUpdate,
  setSearchValueWithUpdate,
} from '../../../../redux/actions/filterActions';
import { setMobileMenuOpen } from '../../../../redux/slices/mobileMenuSlice';
import { selectCartItemsCount } from '../../../../redux/slices/cartSlice';

const MobilePodheader = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [expandedTabIndex, setExpandedTabIndex] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [containerHeight, setContainerHeight] = useState('auto');
  const [isInitialOpen, setIsInitialOpen] = useState(true);

  const tabsSectionRef = useRef(null);
  const filtersSectionRef = useRef(null);

  // Redux selectors
  const tabsData = useSelector((state) => state.tabs_data.tabs);
  const currentTab = useSelector((state) => state.currentTab.currentTab);
  const filterAValue = useSelector((state) => state.currentTab.filter_a_value);
  const filterBValue = useSelector((state) => state.currentTab.filter_b_value);
  const searchValue = useSelector((state) => state.currentTab.search_value);
  const others = useSelector((state) => state.others_data.others);
  const openInFiltersMode = useSelector((state) => state.mobileMenu.openInFiltersMode);
  const cartItemsCount = useSelector(selectCartItemsCount);
  const [randomQuote, setRandomQuote] = useState(null);
  const [headerTabs, setHeaderTabs] = useState([]);

  // Синхронизация локального состояния поиска с Redux
  useEffect(() => {
    setLocalSearchValue(searchValue || '');
  }, [searchValue]);

  // Обработка тегов и фильтрация вкладок для отображения в мобильном меню
  useEffect(() => {
    if (tabsData && tabsData.length > 0) {
      const processed = tabsData
        .map((tab, index) => {
          const tagArray = parseTabTags(tab.tab_tag);
          return {
            ...tab,
            tagArray,
            firstTag: tagArray.length > 0 ? tagArray[0] : null,
          };
        })
        .filter(
          (tab) =>
            tab.tagArray.includes('main_header') ||
            tab.tagArray.includes('header') ||
            tab.tagArray.includes('header_list') ||
            tab.tagArray.includes('header_cart'),
        );
      setHeaderTabs(processed);
    }
  }, [tabsData]);

  // Обработка цитат из others
  useEffect(() => {
    if (others && Array.isArray(others)) {
      const quoteArray = [];
      const quoteData = others.find((el) => el.tab_tag === 'quote');

      if (quoteData && quoteData.data && Array.isArray(quoteData.data)) {
        quoteData.data.forEach((el) => {
          let contentObject = {};
          if (el.content) {
            try {
              if (typeof el.content === 'string') {
                contentObject = JSON.parse(el.content);
              } else {
                contentObject = { ...el.content };
              }
            } catch (error) {
              contentObject = { contentValue: el.content };
            }
          }
          if (contentObject && contentObject[0] && contentObject[0].block_content) {
            quoteArray.push({
              text: contentObject[0].block_content[0]?.content_text || '',
              autor: contentObject[0].block_content[1]?.content_text || '',
            });
          }
        });

        if (quoteArray.length > 0) {
          const randomIndex = Math.floor(Math.random() * quoteArray.length);
          const selectedQuote = quoteArray[randomIndex];
          setRandomQuote(selectedQuote);
        }
      }
    }
  }, [others]);

  // Устанавливаем фиксированную высоту для стабильного переключения
  useEffect(() => {
    // Устанавливаем фиксированную высоту, которая подойдет для большинства случаев
    if (containerHeight === 'auto') {
      setContainerHeight('400px'); // Еще больше увеличена высота
    }
  }, [containerHeight]); // Зависит от containerHeight

  // Получение данных для текущего раздела (как в static_podheader)
  const currentTabData = useSelector((state) => state.currentTab);

  // Получаем данные текущего раздела из tabsData для проверки тегов
  const currentTabFromTabs = tabsData?.find((tab) => tab.eng_name === currentTab?.eng_name);

  // Показываем фильтры и поиск только если есть dynamic подхедер (тег sh_podheader)
  const shouldShowFilters = tabHasTag(currentTabFromTabs?.tab_tag, 'sh_podheader');
  // Показываем цитаты если есть данные цитат (независимо от тегов раздела)
  const shouldShowQuotes = true; // Показываем цитаты всегда, если они есть

  // Функция для получения фильтров конкретного раздела (аналогично header)
  const getFiltersForSection = (sectionName) => {
    const sectionTab = tabsData?.find((tab) => tab.eng_name === sectionName);
    if (sectionTab && sectionTab.publications) {
      let allFilterBValues = ['все'];

      sectionTab.publications.forEach((publication) => {
        if (publication.publication_settings) {
          const isJsonString =
            publication.publication_settings.trim().startsWith('{') ||
            publication.publication_settings.trim().startsWith('[');

          let publicationSettings = {};
          if (isJsonString) {
            try {
              publicationSettings = JSON.parse(publication.publication_settings);
            } catch (error) {}
          }

          if (publicationSettings.publication_filter_b) {
            let filterBValue = publicationSettings.publication_filter_b;
            if (Array.isArray(filterBValue)) {
              filterBValue = filterBValue.join(', ');
            }
            if (typeof filterBValue === 'string' && filterBValue.trim() !== '') {
              if (!allFilterBValues.includes(filterBValue)) {
                allFilterBValues.push(filterBValue);
              }
            }
          }
        }
      });

      return allFilterBValues;
    }
    return [];
  };

  // Получение данных для фильтров (как в static_podheader)
  const publicationFilterA = currentTabData?.publication_filter_a || [];
  const publicationFilterB = currentTabData?.publication_filter_b || [];

  // Функция для смены цитаты
  const changeQuote = () => {
    if (others && Array.isArray(others)) {
      const quoteArray = [];
      const quoteData = others.find((el) => el.tab_tag === 'quote');

      if (quoteData && quoteData.data && Array.isArray(quoteData.data)) {
        quoteData.data.forEach((el) => {
          let contentObject = {};
          if (el.content) {
            try {
              if (typeof el.content === 'string') {
                contentObject = JSON.parse(el.content);
              } else {
                contentObject = { ...el.content };
              }
            } catch (error) {
              contentObject = { contentValue: el.content };
            }
          }
          if (contentObject && contentObject[0] && contentObject[0].block_content) {
            quoteArray.push({
              text: contentObject[0].block_content[0]?.content_text || '',
              autor: contentObject[0].block_content[1]?.content_text || '',
            });
          }
        });

        if (quoteArray.length > 0) {
          const randomIndex = Math.floor(Math.random() * quoteArray.length);
          const selectedQuote = quoteArray[randomIndex];
          setRandomQuote(selectedQuote);
        }
      }
    }
  };

  const handleTabClick = (tab) => {
    dispatch(resetFilters());
    dispatch(setPaginationPage(1));
    dispatch(setCurrentTab(tab));
    navigate(`/${tab.eng_name === 'main_tab' ? '' : tab.eng_name}`);

    // Меняем цитату при смене раздела
    changeQuote();

    dispatch(setMobileMenuOpen(false));
  };

  const handleFilterAChange = (value) => {
    dispatch(setFilterAValueWithUpdate(value));
    dispatch(setMobileMenuOpen(false)); // Закрываем мобильный подхедер после изменения фильтра
  };

  const handleFilterBChange = (value) => {
    dispatch(setFilterBValueWithUpdate(value));
    dispatch(setMobileMenuOpen(false)); // Закрываем мобильный подхедер после изменения фильтра
  };

  const handleSearchChange = (e) => {
    setLocalSearchValue(e.target.value);
  };

  const handleSearchSubmit = () => {
    dispatch(setSearchValueWithUpdate(localSearchValue));
    setLocalSearchValue('');
    dispatch(setMobileMenuOpen(false)); // Закрываем мобильный подхедер после поиска
  };

  const handleDropdownItemClick = (tabEngName, filterValue) => {
    // Устанавливаем флаг пропуска сброса фильтров
    dispatch(setSkipFilterReset(true));

    // Устанавливаем фильтр B
    dispatch(setFilterBValueWithUpdate(filterValue));

    // Находим нужную вкладку
    const targetTab = tabsData?.find((tab) => tab.eng_name === tabEngName);
    if (targetTab) {
      dispatch(setCurrentTab(targetTab));
      navigate(`/${targetTab.eng_name === 'main_tab' ? '' : targetTab.eng_name}`);

      // Меняем цитату при смене раздела
      changeQuote();
    }

    // Сбрасываем флаг после небольшой задержки
    setTimeout(() => {
      dispatch(setSkipFilterReset(false));
    }, 200);

    // Закрываем все выпадающие списки
    setExpandedTabIndex(null);
    dispatch(setMobileMenuOpen(false));
  };

  const toggleTabExpansion = (index) => {
    setExpandedTabIndex(expandedTabIndex === index ? null : index);
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
    // Отмечаем, что переключение происходит вручную
    setIsInitialOpen(false);
  };

  // Закрываем выпадающие списки и возвращаемся к разделам при закрытии подхедера
  // При открытии с флагом openInFiltersMode сразу устанавливаем режим фильтров
  useEffect(() => {
    if (!isOpen) {
      setExpandedTabIndex(null);
      setShowFilters(false);
      setIsInitialOpen(true); // Сбрасываем флаг при закрытии
    } else {
      // При открытии устанавливаем isInitialOpen в true
      setIsInitialOpen(true);

      if (openInFiltersMode && shouldShowFilters) {
        // Сразу устанавливаем режим фильтров без анимации
        setShowFilters(true);
      }
    }
  }, [isOpen, openInFiltersMode, shouldShowFilters]);

  if (!isOpen) return null;

  return (
    <div className={styles.mobilePodheader}>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.content}>
        {/* Заголовок с кнопкой закрытия */}
        <div className={styles.header}>
          <h2>{(currentTab?.show_name || currentTab?.tab_name || 'Раздел').toLowerCase()}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Цитаты (показываем всегда если есть static подхедер) */}
        {shouldShowQuotes && randomQuote && (
          <div className={styles.quoteSection}>
            <h3>Цитата</h3>
            <div className={styles.quoteContent}>
              <p className={styles.quoteText}>"{randomQuote.text}"</p>
              <p className={styles.quoteAuthor}>— {randomQuote.autor}</p>
            </div>
          </div>
        )}

        {/* Контейнер для разделов и фильтров */}
        <div className={styles.contentSection}>
          {/* Контент с плавными переходами */}
          <div
            className={`${styles.contentWrapper} ${
              openInFiltersMode && isInitialOpen ? styles.noTransition : ''
            }`}>
            {/* Контент разделов */}
            <div
              ref={tabsSectionRef}
              className={`${styles.tabsSection} ${
                !showFilters ? styles.activeContent : styles.inactiveContent
              }`}>
              <div className={styles.sectionTitleContainer}>
                <h3 className={styles.sectionTitle}>Разделы</h3>
                {shouldShowFilters && (
                  <button className={styles.switchButtonCompact} onClick={handleToggleFilters}>
                    ⌕
                  </button>
                )}
              </div>
              <div className={styles.tabsList}>
                {headerTabs.map((tab, index) => {
                  const hasHeaderList = tab.tagArray?.includes('header_list');
                  const hasHeaderCart = tab.tagArray?.includes('header_cart');
                  const sectionFilters = hasHeaderList ? getFiltersForSection(tab.eng_name) : [];
                  // Показываем выпадающее меню только если есть header_list И больше 1 фильтра
                  const shouldShowDropdown = hasHeaderList && sectionFilters.length > 1;
                  const isExpanded = expandedTabIndex === index;

                  return (
                    <div key={tab.eng_name} className={styles.tabContainer}>
                      <div className={styles.tabHeader}>
                        <button
                          className={`${styles.tabButton} ${
                            currentTab?.eng_name === tab.eng_name ? styles.active : ''
                          } ${hasHeaderCart && cartItemsCount > 0 ? styles.cartText : ''}`}
                          onClick={() => handleTabClick(tab)}>
                          {tab.show_name.toLowerCase()}
                          {hasHeaderCart && cartItemsCount > 0 && (
                            <span className={styles.cartBadge}>{cartItemsCount}</span>
                          )}
                        </button>
                        {shouldShowDropdown && (
                          <button
                            className={`${styles.expandButton} ${
                              isExpanded ? styles.expanded : ''
                            }`}
                            onClick={() => toggleTabExpansion(index)}>
                            {isExpanded ? '▲' : '▼'}
                          </button>
                        )}
                      </div>
                      {shouldShowDropdown && isExpanded && (
                        <div className={styles.dropdownList}>
                          {sectionFilters.map((filterValue, filterIndex) => (
                            <button
                              key={filterIndex}
                              className={styles.dropdownItem}
                              onClick={() => handleDropdownItemClick(tab.eng_name, filterValue)}>
                              {formatFilterText(filterValue)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Контент фильтров */}
            {shouldShowFilters && (
              <div
                ref={filtersSectionRef}
                className={`${styles.filtersSection} ${
                  showFilters ? styles.activeContent : styles.inactiveContent
                }`}>
                <div className={styles.sectionTitleContainer}>
                  <h3 className={styles.sectionTitle}>Поиск и фильтры</h3>
                  <button className={styles.switchButtonCompact} onClick={handleToggleFilters}>
                    ←
                  </button>
                </div>
                {/* Поиск */}
                <div className={styles.searchContainer}>
                  <label className={styles.searchLabel}>Поиск по разделу:</label>
                  <div className={styles.searchInputContainer}>
                    <input
                      type='text'
                      className={styles.searchInput}
                      placeholder='Введите поисковый запрос...'
                      value={localSearchValue}
                      onChange={handleSearchChange}
                    />
                    <button className={styles.searchButton} onClick={handleSearchSubmit}>
                      →
                    </button>
                  </div>
                </div>

                {/* Фильтр A */}
                {publicationFilterA.length > 1 && (
                  <div className={styles.filterContainer}>
                    <label className={styles.filterLabel}>тип деформации:</label>
                    <select
                      className={styles.filterSelect}
                      value={filterAValue}
                      onChange={(e) => handleFilterAChange(e.target.value)}>
                      {publicationFilterA.map((option, index) => (
                        <option key={index} value={option}>
                          {formatFilterText(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Фильтр B */}
                {publicationFilterB.length > 1 && (
                  <div className={styles.filterContainer}>
                    <label className={styles.filterLabel}>показать:</label>
                    <select
                      className={styles.filterSelect}
                      value={filterBValue}
                      onChange={(e) => handleFilterBChange(e.target.value)}>
                      {publicationFilterB.map((option, index) => (
                        <option key={index} value={option}>
                          {formatFilterText(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilePodheader;
